import { useEffect, useState } from "react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import { MARKETPLACE_FILTERS, COUNTRIES } from "@/lib/catalog";

const CATEGORIES = MARKETPLACE_FILTERS;

export default function Marketplace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");
  const [states, setStates] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");

  const [orderProduct, setOrderProduct] = useState(null);
  const [orderForm, setOrderForm] = useState({ dropoff_city: "", distance_miles: 5, tip: 0, quantity: 1 });

  useEffect(() => {
    api.get("/states").then((r) => setStates(r.data));
  }, []);

  useEffect(() => {
    const params = {};
    if (category !== "all") params.category = category;
    if (stateFilter !== "all") params.state = stateFilter;
    if (countryFilter !== "all") params.country = countryFilter;
    api.get("/products", { params }).then((r) => setProducts(r.data));
  }, [category, stateFilter, countryFilter]);

  const filtered = products.filter((p) =>
    !search ||
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    p.description.toLowerCase().includes(search.toLowerCase())
  );

  const startOrder = (product) => {
    if (!user) {
      toast.info("Please sign in to place an order.");
      navigate("/login");
      return;
    }
    setOrderProduct(product);
    setOrderForm({ dropoff_city: user.state || "", distance_miles: 5, tip: 0, quantity: 1 });
  };

  const placeOrder = async () => {
    try {
      await api.post("/orders", {
        product_id: orderProduct.product_id,
        quantity: parseInt(orderForm.quantity) || 1,
        dropoff_city: orderForm.dropoff_city,
        distance_miles: parseFloat(orderForm.distance_miles) || 0,
        tip: parseFloat(orderForm.tip) || 0,
      });
      toast.success("Order placed. A driver will pick it up soon.");
      setOrderProduct(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place order.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-12 py-12">
      <Seo
        url="/marketplace"
        title="Marketplace"
        description="Browse fresh produce, pasture-raised meats, eggs, and mutation produce direct from verified hobby farmers in your state."
        image="https://azuregypsy.org/og-marketplace.jpg"
      />
      <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Marketplace</div>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-stone-900">From the field, this morning.</h1>
      <p className="mt-3 font-sans text-stone-600 max-w-2xl">
        Browse what's ready for pickup in your state. Look for "Mutation" listings — beautifully imperfect produce from hobby farmers, often free or for barter.
      </p>

      {/* Filters */}
      <div className="mt-10 flex flex-col lg:flex-row gap-6">
        <div className="lg:w-64 shrink-0">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-3">Categories</div>
          <div className="flex flex-wrap lg:flex-col gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`cat-${c.id}`}
                onClick={() => setCategory(c.id)}
                className={`text-left px-4 py-2 rounded-md font-sans text-sm transition-colors ${
                  category === c.id ? "bg-emerald-800 text-stone-50" : "bg-white border border-stone-200 text-stone-700 hover:border-stone-400"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <Input
              data-testid="search-input"
              placeholder="Search produce, meats..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-white border-stone-200"
            />
            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger data-testid="country-filter" className="bg-white border-stone-200">
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All Countries</SelectItem>
                {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger data-testid="state-filter" className="bg-white border-stone-200">
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States / Regions</SelectItem>
                {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-md p-12 text-center">
              <div className="font-serif text-2xl text-stone-700">Nothing here yet.</div>
              <p className="mt-2 text-stone-500 font-sans text-sm">Check back soon — or try a different filter.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <ProductCard key={p.product_id} product={p} onOrder={startOrder} />
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!orderProduct} onOpenChange={(o) => !o && setOrderProduct(null)}>
        <DialogContent className="bg-stone-50">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Place Order — {orderProduct?.title}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Quantity</label>
              <Input data-testid="order-quantity" type="number" min={1} value={orderForm.quantity} onChange={(e) => setOrderForm({ ...orderForm, quantity: e.target.value })} className="mt-2 bg-white" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Drop-off City</label>
              <Input data-testid="order-dropoff" value={orderForm.dropoff_city} onChange={(e) => setOrderForm({ ...orderForm, dropoff_city: e.target.value })} className="mt-2 bg-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Distance (mi)</label>
                <Input data-testid="order-distance" type="number" value={orderForm.distance_miles} onChange={(e) => setOrderForm({ ...orderForm, distance_miles: e.target.value })} className="mt-2 bg-white" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Driver Tip ($)</label>
                <Input data-testid="order-tip" type="number" value={orderForm.tip} onChange={(e) => setOrderForm({ ...orderForm, tip: e.target.value })} className="mt-2 bg-white" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="confirm-order-btn" onClick={placeOrder} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">Confirm Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
